import type { HolidayCreateSchemaInput } from '@repo/schemas/holiday/createHoliday';

import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { useCreateHoliday } from '@repo/api/holiday/useCreateHoliday';
import { baseErrorMessages } from '@repo/lib/messages/baseErrorMessages';
import { holidayCreateSchema } from '@repo/schemas/holiday/createHoliday';
import { addDays, differenceInDays, format, parseISO } from 'date-fns';
import { CheckCircle2, CalendarDaysIcon } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { FeaturedIcon } from '@/components/ui/featured-icon';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { LoadingOverlay, LoadingOverlayLabel, LoadingOverlaySpinner } from '@/components/ui/loading-overlay';
import { LoadingSwap } from '@/components/ui/loading-swap';
import { toasterMessageHandler } from '@/lib/toaster-message-handler';

/**
 * Props for the CreateHolidayDialog component.
 * @property {boolean} open - Whether the dialog is open.
 * @property {(value: boolean) => void} onOpenChange - Callback to change dialog open state.
 */
interface CreateHolidayDialogProps {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  defaultValues?: Partial<HolidayCreateSchemaInput>;
}

export default function CreateHolidayDialog(createHolidayDialogProps: CreateHolidayDialogProps) {
  const { open, onOpenChange, defaultValues } = createHolidayDialogProps;
  const { mutateAsync, isPending } = useCreateHoliday();

  const form = useForm<HolidayCreateSchemaInput>({
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      ...defaultValues,
    },
    resolver: standardSchemaResolver(holidayCreateSchema.in),
  });

  const onSubmit = (data: HolidayCreateSchemaInput) => {
    mutateAsync(data)
      .then(() => {
        onClose();
      })
      .catch((error) => {
        toasterMessageHandler(baseErrorMessages, error);
      });
  };

  const onClose = () => {
    onOpenChange(false);
    form.reset();
  };

  const handleOnOpenChange = (value: boolean) => {
    if (!value) {
      onClose();
    } else {
      onOpenChange(true);
    }
  };

  const handleStartDatePickerSelect = (date: Date) => {
    const oldStart = form.getValues('startDate');
    const oldEnd = form.getValues('endDate');
    const dateStr = format(date, 'yyyy-MM-dd');
    if (oldEnd && date > parseISO(oldEnd) && oldStart) {
      const offsetDays = differenceInDays(parseISO(oldEnd), parseISO(oldStart));
      form.setValue('endDate', format(addDays(date, offsetDays), 'yyyy-MM-dd'), {
        shouldValidate: true,
      });
    }
    return dateStr;
  };

  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
    }
  }, [open, defaultValues, form]);

  return (
    <Dialog open={open} onOpenChange={handleOnOpenChange}>
      <DialogContent className={'sm:max-w-lg'}>
        <Form {...form}>
          <DialogBody>
            <LoadingOverlay isLoading={isPending}>
              <LoadingOverlaySpinner />
              <LoadingOverlayLabel>Trwa tworzenie dnia wolnego...</LoadingOverlayLabel>
            </LoadingOverlay>
            <DialogHeader className={'border-border-primary bg-card items-center gap-2 border-b'}>
              <FeaturedIcon>
                <CalendarDaysIcon />
              </FeaturedIcon>
              <div className={'flex min-w-0 grow flex-col gap-0.5'}>
                <DialogTitle>Utwórz dzień wolny</DialogTitle>
                <DialogDescription className={'text-text-tertiary text-xs'}>Wprowadź daty dotyczące dni wolnych.</DialogDescription>
              </div>
            </DialogHeader>
            <form id={'create-holiday-form'} className={'flex grow flex-col gap-3 overflow-auto p-4'} onSubmit={form.handleSubmit(onSubmit)} noValidate>
              <FormField
                name={'name'}
                control={form.control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nazwa</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={'Nazwa'} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className={'grid gap-2 sm:grid-cols-2'}>
                <FormField
                  name={'startDate'}
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data początkowa</FormLabel>
                      <FormControl>
                        <DatePicker
                          {...field}
                          selected={field.value ? new Date(field.value) : null}
                          onSelect={(date) => {
                            field.onChange(handleStartDatePickerSelect(date));
                            field.onBlur();
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  name={'endDate'}
                  control={form.control}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Data końcowa</FormLabel>
                      <FormControl>
                        <DatePicker
                          {...field}
                          selected={field.value ? new Date(field.value) : null}
                          onSelect={(date) => {
                            field.onChange(format(date, 'yyyy-MM-dd'));
                            field.onBlur();
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </form>
          </DialogBody>
          <DialogFooter className={'border-border-primary dark:bg-card bg-accent border-t'}>
            <Button onClick={onClose} type={'button'} variant={'tertiary'} className={'w-full md:w-fit'}>
              Anuluj
            </Button>
            <Button disabled={isPending} form={'create-holiday-form'} type={'submit'} variant={'primary'} className={'w-full md:w-fit'}>
              <LoadingSwap isLoading={isPending} className="flex items-center gap-2">
                <CheckCircle2 />
                Zapisz
              </LoadingSwap>
            </Button>
          </DialogFooter>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
